import {useCallback, useEffect, useRef, useState} from "react";
import {BaSeCheckbox, BaSeInput, BaSeOptionPicker} from "@base/react";
import {FILTER_OPTIONS} from "../../../mocks/consts";
import ApiLab from "../../../services/lab";
import moment from "moment";
import {eventTag} from "../../../services/utils";
import {useDebounced} from "../../../hooks/use-debounce";
import './index.scss';

const apiLab = ApiLab();

const FAKE_PLANNING = {
  id: "__onboarding_fake__",
  title: "Planejamento A",
  finished_at: moment().format("DD/MM/YYYY HH:mm:ss"),
};

const useLabPlannings = (onOnboardingReady, isOnboarding, eventLabGA) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [monthFilter, setMonthFilter] = useState(null);
  const [completedPlannings, setCompletedPlannings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const onOnboardingReadyRef = useRef(onOnboardingReady);

  useEffect(() => {
    onOnboardingReadyRef.current = onOnboardingReady;
  }, [onOnboardingReady]);

  const fetchPlannings = useCallback(() => {
    if (isOnboarding) return;

    setIsLoading(true);
    const params = { page_size: "all" };
    if (searchTerm) params.plan_title = searchTerm;
    if (monthFilter) {
      params.start_date = monthFilter;
      params.end_date = moment().format("YYYY-MM-DD");
    }

    apiLab.getFinishedPlannings(params)
      .then((response) => {
        const results = response.data.results;
        if (response.data.onboarding_lab === false && results.length > 0) {
          onOnboardingReadyRef.current?.();
        }
        setCompletedPlannings(results.length > 0 ? results : []);
      })
      .catch((error) => console.error("Error fetching plannings:", error))
      .finally(() => setIsLoading(false));
  }, [searchTerm, monthFilter, isOnboarding]);

  useEffect(() => { fetchPlannings(); }, [fetchPlannings]);

  const filterMonths = (selectedFilter) => {
    eventTag(eventLabGA?.name, eventLabGA?.category, "submit", `Filtro por: ${selectedFilter?.label}`)
    setMonthFilter(
      selectedFilter.monthAmt === 0
        ? null
        : moment().subtract(selectedFilter.monthAmt, "months").format("YYYY-MM-DD")
    );
  };

  return { searchTerm, setSearchTerm, completedPlannings, isLoading, filterMonths };
};

const PlanningSelectionStep = ({
                                 selectedPlannings,
                                 togglePlanning,
                                 onOnboardingReady,
                                 isOnboarding,
                                 eventLabGA,
                               }) => {
  const [inputValue, setInputValue] = useState("");

  const { setSearchTerm, completedPlannings, isLoading, filterMonths } = useLabPlannings(onOnboardingReady, isOnboarding, eventLabGA);
  const [debouncedSetSearch] = useDebounced(setSearchTerm, 500);

  const handleSearchChange = (value) => {
    setInputValue(value);
    debouncedSetSearch(value);
    eventTag(eventLabGA?.name, eventLabGA?.category, "submit", `Pesquisando`)
  };

  const displayedPlannings = isOnboarding
    ? [FAKE_PLANNING]
    : completedPlannings;

  const isEmpty = !isLoading && !isOnboarding && completedPlannings.length === 0;

  return (
    <div className="planning-selection-container">
      <div className="planning-selection-filters">
        <BaSeInput
          placeholder="Pesquise por nome do planejamento"
          value={inputValue}
          onChange={handleSearchChange}
          width={597}
          iconButton={{
            typeButton: "base-button",
            type: "secondary",
            name: "search",
            color: "#005EB8"
          }}
        />
        <BaSeOptionPicker
          label="Filtrar por data de conclusão"
          values={FILTER_OPTIONS}
          enableSelectAll={false}
          maxOptionsSelected={1}
          withFilterText={false}
          onChange={(e) => filterMonths(e[0])}
          width={290}
        />
      </div>
      <div className="planning-selection-grid">
        {isLoading ? (
          <div>Carregando...</div>
        ) : isEmpty ? (
          <div>Você não possui planejamentos concluídos.</div>
        ) : (
          displayedPlannings.map((item) => (
            <div key={item.id} className="planning-selection-card">
              <BaSeCheckbox
                checked={selectedPlannings.some(planning => planning.id === item.id)}
                onChange={() => togglePlanning(item)}
                shape="square"
              />
              <div className="planning-selection-card-text">
                <h4>{item.title}</h4>
                <small>{`(Concluído em: ${moment(item.finished_at, "DD/MM/YYYY HH:mm:ss").format("DD/MM/YYYY")})`}</small>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export default PlanningSelectionStep;
